import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { normalizeDoc } from '../domain/document';
import type { Flow, FlowDoc } from '../domain/types';
import { flowsApi } from '../infrastructure/api';
import { flowKeys } from './queries';

export const AUTOSAVE_MS = 1200;

export interface Draft {
  name: string;
  description: string;
  doc: FlowDoc;
}

export type SaveState = 'saved' | 'pending' | 'saving' | 'error';

/**
 * O rascunho do builder, salvo sozinho ~1,2 s depois da última mudança (ou
 * já, com `flush` — Ctrl/Cmd+S, antes de publicar, ao sair).
 *
 * A edição local manda: a resposta do PUT só traz o que é do servidor
 * (problemas, versões, "alterações não publicadas") e nunca sobrescreve o
 * que a pessoa já mudou enquanto a gravação ia e voltava. As gravações vão
 * em fila, uma por vez — uma resposta antiga nunca chega depois da nova.
 */
export function useFlowDraft(initial: Flow) {
  const qc = useQueryClient();
  const flowId = initial.id;
  const [draft, setDraft] = useState<Draft>(() => ({
    name: initial.name,
    description: initial.description,
    doc: normalizeDoc(initial.definition),
  }));
  const [server, setServer] = useState<Flow>(initial);
  /** O último rascunho que a API confirmou. */
  const [acked, setAcked] = useState<Draft>(draft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const latest = useRef(draft);
  const ackedRef = useRef(acked);
  const queued = useRef<Draft | null>(null);
  const chain = useRef<Promise<boolean>>(Promise.resolve(true));
  useLayoutEffect(() => {
    latest.current = draft;
    ackedRef.current = acked;
  });

  /** Grava o rascunho atual (entra na fila). Resolve `true` quando ele — ou um mais novo — foi salvo. */
  const flush = useCallback((): Promise<boolean> => {
    const snapshot = latest.current;
    if (snapshot === ackedRef.current && !queued.current) return chain.current;
    queued.current = snapshot;
    chain.current = chain.current.then(async () => {
      // Já saiu numa gravação anterior, ou um rascunho mais novo vai logo atrás.
      if (queued.current !== snapshot) return true;
      queued.current = null;
      setSaving(true);
      setError(null);
      try {
        const out = await flowsApi.save(flowId, {
          name: snapshot.name,
          description: snapshot.description,
          definition: snapshot.doc,
        });
        ackedRef.current = snapshot;
        setAcked(snapshot);
        setServer(out);
        qc.setQueryData(flowKeys.one(flowId), out);
        void qc.invalidateQueries({ queryKey: flowKeys.list() });
        return true;
      } catch (e) {
        setError(e);
        return false;
      } finally {
        setSaving(false);
      }
    });
    return chain.current;
  }, [flowId, qc]);

  // Autosave: cada mudança reinicia a contagem.
  useEffect(() => {
    if (draft === acked) return;
    const timer = window.setTimeout(() => void flush(), AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, acked, flush]);

  // Ao sair do builder com algo por salvar: grava sem esperar; fechar a aba pede confirmação.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (latest.current !== ackedRef.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      if (latest.current !== ackedRef.current) void flush();
    };
  }, [flush]);

  const updateDoc = useCallback((fn: (doc: FlowDoc) => FlowDoc) => {
    setDraft((d) => {
      const doc = fn(d.doc);
      return doc === d.doc ? d : { ...d, doc };
    });
  }, []);

  const setMeta = useCallback((patch: Partial<Pick<Draft, 'name' | 'description'>>) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const saveState: SaveState = error ? 'error' : saving ? 'saving' : draft !== acked ? 'pending' : 'saved';

  return { draft, server, setServer, updateDoc, setMeta, flush, saveState, saveError: error };
}

export type FlowDraftApi = ReturnType<typeof useFlowDraft>;
